import React from 'react';
import { DatoCmsAdvisorLeadFormSectionComponent, DatoCmsBrandLogoSectionComponent, DatoCmsCoverageDetailsGrid, DatoCmsEoFormSectionComponent, DatoCmsFaqList, DatoCmsHeroComponent, DatoCmsInformationSectionComponent, DatoCmsListComponent, DatoCmsMadLibsSectionComponent, DatoCmsNavigationBar, DatoCmsProductComparisonTable, PageComponents } from '../../schema/datocms';
import { ComponentTypeNames } from '../../schema';
import { DividerLines } from '../../schema/enum/divider-lines';
import HeroComponent from '../page-components/hero-component/hero-component';
import ListComponent from '../page-components/list-component/list-component';
import InformationSectionComponent from '../page-components/information-section-component/information-section-component';
import MadLibsSectionComponent from '../page-components/mad-libs-section-component/mad-libs-section-component';
import BrandLogoSectionComponent from '../page-components/brand-logo-section-component/brand-logo-section-component';
import EoFormSectionComponent from '../page-components/eo-form-section-component/eo-form-section-component';
import ProductComparisonTableComponent from '../page-components/product-comparison-table-component/product-comparison-table-component';
import CoverageDetailsGridComponent from '../page-components/coverage-details-grid-component/coverage-details-grid-component';
import FaqListComponent from '../page-components/faq-list-component/faq-list-component';
import NavigationBarComponent from '../page-components/navigation-bar-component/navigation-bar-component';
import AdvisorLeadFormSectionComponent from '../page-components/advisor-lead-form-section-component/advisor-lead-form-section-component';

export interface ModularContentAreaProps {
    components: PageComponents[];
    currentPageSlug?: string;
}

// This component serves as the index for all components that can be used to compose a page
const ModularContentArea = ({ components, currentPageSlug }: ModularContentAreaProps): React.ReactElement => {
    return (
        <>
            {
                components.map((component, index) => {
                    const { __typename, dividerLine } = component;

                    let result = null;
                    switch(__typename) {
                        case ComponentTypeNames.HERO_COMPONENT:
                            result = <div className={'min-h-[700px]'}><HeroComponent key={index} {...component as DatoCmsHeroComponent} /></div>;
                            break;
                        case ComponentTypeNames.LIST_COMPONENT:
                            result = <ListComponent {...component as DatoCmsListComponent} />;
                            break;
                        case ComponentTypeNames.INFORMATION_SECTION:
                            result = <InformationSectionComponent {...component as DatoCmsInformationSectionComponent} />;
                            break;
                        case ComponentTypeNames.MAD_LIBS_SECTION:
                            result = <MadLibsSectionComponent {...component as DatoCmsMadLibsSectionComponent } />;
                            break;
                        case ComponentTypeNames.BRAND_LOGO_SECTION:
                            result = <BrandLogoSectionComponent {...component as DatoCmsBrandLogoSectionComponent} />;
                            break;
                        case ComponentTypeNames.EO_FORM:
                            result = <EoFormSectionComponent {...component as DatoCmsEoFormSectionComponent } />;
                            break;
                        case ComponentTypeNames.PRODUCT_COMPARISON_TABLE:
                            result = <ProductComparisonTableComponent {...component as DatoCmsProductComparisonTable } />;
                            break;
                        case ComponentTypeNames.COVERAGE_DETAILS_GRID:
                            result = <CoverageDetailsGridComponent {...component as DatoCmsCoverageDetailsGrid} />;
                            break;
                        case ComponentTypeNames.FAQ_LIST:
                            result = <FaqListComponent {...component as DatoCmsFaqList} />;
                            break;
                        case ComponentTypeNames.NAVIGATION_BAR:
                            result = <NavigationBarComponent currentPageSlug={currentPageSlug} {...component as DatoCmsNavigationBar} />;
                            break;
                        case ComponentTypeNames.ADVISOR_LEAD_FORM:
                            result = <AdvisorLeadFormSectionComponent {...component as DatoCmsAdvisorLeadFormSectionComponent } />;
                            break;
                        default:
                            console.warn('Unsupported page component', __typename);
                            result = null;
                            break;
                    }

                    let previousComponentBottomDivider: boolean = false;
                    if (index > 0) {
                        const previousComponent: PageComponents = components[index - 1];
                        previousComponentBottomDivider = previousComponent?.dividerLine === DividerLines.BELOW || previousComponent?.dividerLine === DividerLines.BOTH;
                    }
                    const topDividerShows: boolean = !previousComponentBottomDivider && dividerLine && (dividerLine === DividerLines.ABOVE || dividerLine === DividerLines.BOTH);
                    const bottomDividerShows: boolean = dividerLine && (dividerLine === DividerLines.BELOW || dividerLine === DividerLines.BOTH);

                    return (
                        <React.Fragment key={index}>
                            <hr className={topDividerShows ? '' : 'hidden'} />

                            <div id={component.componentId}>
                                {result}
                            </div>

                            <hr className={bottomDividerShows ? '' : 'hidden'} />
                        </React.Fragment>
                    );
                })
            }
        </>
    );
};

export default ModularContentArea;
